import { PaginationDirection } from "services/url";
import { Pagination } from "services/search/types";
import { BADGE_DOT_COLORS } from "ui/components/Badge/consts";
import { CardSelectOption } from "ui/components/CardSelect/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TemplateTag } from "ui/components/TemplateEditor/types";
import {
  NotificationDefinitionFormValues,
  NotificationSearchFormValues,
  NotificationLogSearchFormValues,
  NotificationEntityType,
  NotificationChannel,
  NotificationScope,
} from "./types";

/*---------------------------------------------------------------
   INITIAL FORM VALUES
-----------------------------------------------------------------*/
export const initialNotificationDefinitionForm: NotificationDefinitionFormValues = {
  title: "",
  messageTemplate: "",
  entityType: null,
  scope: NotificationScope.Group,
  channels: 0,
  isCustom: false,
  customScheduledAt: null,
  includedRoleIds: [],
  includedUserIds: [],
  excludedUserIds: [],
  excludedEntityIds: [],
  includedEntityIds: [],
  daysBeforeExpiry: null,
  isRepeating: false,
  repeatIntervalDays: null,
  repeatCount: null,
  notifyIfDateMissing: false,
};

export const initialNotificationSearchForm: NotificationSearchFormValues = {
  entityType: null,
  isActive: null,
  scope: null,
  channel: null,
  isCustom: null,
  search: null,
};

/*---------------------------------------------------------------
   PAGINATION
-----------------------------------------------------------------*/
export const initialNotificationPagination: Pagination = {
  pageNumber: 1,
  pageSize: 25,
  sort: {
    sortBy: "created",
    direction: PaginationDirection.Descending,
  },
};

export const initialEntityItemPagination: Pagination = {
  pageNumber: 1,
  pageSize: 20,
  sort: {
    sortBy: "displayName",
    direction: PaginationDirection.Ascending,
  },
};

/*---------------------------------------------------------------
   ENTITY TYPE LABELS
-----------------------------------------------------------------*/
export const ENTITY_TYPE_LABELS: Record<number, string> = {
  [NotificationEntityType.FireExtinguisher]: "Vatrogasni aparat",
  [NotificationEntityType.VehicleRegistration]: "Registracija vozila",
  [NotificationEntityType.VehicleInsurance]: "Osiguranje vozila",
  [NotificationEntityType.Offer]: "Ponuda",
  [NotificationEntityType.Contract]: "Ugovor",
  [NotificationEntityType.Invoice]: "Račun",
  [NotificationEntityType.Custom]: "Prilagođeno",
};

/*---------------------------------------------------------------
   ENTITY TYPE ICONS
-----------------------------------------------------------------*/
export const ENTITY_TYPE_ICONS: Record<number, string> = {
  [NotificationEntityType.FireExtinguisher]: "/media/icons/fire-extinguisher.svg",
  [NotificationEntityType.VehicleRegistration]: "/media/icons/vehicle.svg",
  [NotificationEntityType.VehicleInsurance]: "/media/icons/document.svg",
  [NotificationEntityType.Offer]: "/media/icons/offer.svg",
  [NotificationEntityType.Contract]: "/media/icons/contract.svg",
  [NotificationEntityType.Invoice]: "/media/icons/invoice.svg",
  [NotificationEntityType.Custom]: "/media/icons/setting.svg",
};

/*---------------------------------------------------------------
   ENTITY TYPE DESCRIPTIONS
-----------------------------------------------------------------*/
/** Što svaki tip prati — prati zadane predloške poruka niže u datoteci. */
export const ENTITY_TYPE_DESCRIPTIONS: Record<number, string> = {
  [NotificationEntityType.FireExtinguisher]: "Podsjetnik prije isteka vatrogasnog aparata.",
  [NotificationEntityType.VehicleRegistration]: "Podsjetnik prije isteka registracije vozila.",
  [NotificationEntityType.VehicleInsurance]: "Podsjetnik prije isteka police osiguranja.",
  [NotificationEntityType.Offer]: "Podsjetnik prije isteka ponude.",
  [NotificationEntityType.Contract]: "Podsjetnik prije isteka ugovora.",
  [NotificationEntityType.Invoice]: "Podsjetnik prije dospijeća računa.",
  [NotificationEntityType.Custom]: "Vlastito pravilo — sami birate datum i tekst poruke.",
};

/*---------------------------------------------------------------
   ENTITY TYPE CARD OPTIONS
-----------------------------------------------------------------*/
/**
 * Tipovi entiteta za `CardSelect`. Id je string jer ga komponenta tako traži — pozivatelj ga
 * pri odabiru vraća u broj.
 */
export const ENTITY_TYPE_CARD_OPTIONS: CardSelectOption[] = Object.entries(
  ENTITY_TYPE_LABELS
).map(([value, label]) => ({
  id: value,
  title: label,
  icon: ENTITY_TYPE_ICONS[Number(value)],
  description: ENTITY_TYPE_DESCRIPTIONS[Number(value)],
}));

/*---------------------------------------------------------------
   CHANNEL OPTIONS (for checkboxes)
-----------------------------------------------------------------*/
export const CHANNEL_OPTIONS = [
  { value: NotificationChannel.Email, label: "Email", icon: "/media/icons/email.svg", variant: "info" as const },
  { value: NotificationChannel.SMS, label: "SMS", icon: "/media/icons/message.svg", variant: "success" as const },
  { value: NotificationChannel.Push, label: "Push", icon: "/media/icons/notification.svg", variant: "warning" as const },
];

export const CHANNEL_BADGE_MAP: Record<string, { icon: string; variant: "info" | "warning" | "danger" | "success" }> = {
  Email: { icon: "/media/icons/email.svg", variant: "info" },
  SMS: { icon: "/media/icons/message.svg", variant: "success" },
  Push: { icon: "/media/icons/notification.svg", variant: "warning" },
};

/*---------------------------------------------------------------
   FILTER OPTIONS (pretraga notifikacija)
-----------------------------------------------------------------*/
/**
 * Točka uz stavku filtra nosi istu boju kao badge te vrijednosti u tablici — boje idu iz
 * `BADGE_DOT_COLORS`, da filtar i redak ne odu u različite nijanse.
 *
 * Opseg i vrsta se u tablici prikazuju kao običan tekst, bez badgea, pa nemaju ni boju.
 */
export const ACTIVE_FILTER_OPTIONS: DropdownOption[] = [
  { value: "true", text: "Aktivna", color: BADGE_DOT_COLORS.success },
  { value: "false", text: "Neaktivna", color: BADGE_DOT_COLORS.danger },
];

export const SCOPE_FILTER_OPTIONS: DropdownOption[] = [
  { value: String(NotificationScope.Group), text: "Grupno" },
  { value: String(NotificationScope.Individual), text: "Individualno" },
];

export const CHANNEL_FILTER_OPTIONS: DropdownOption[] = [
  {
    value: String(NotificationChannel.Email),
    text: "Email",
    color: BADGE_DOT_COLORS[CHANNEL_BADGE_MAP.Email.variant],
  },
  {
    value: String(NotificationChannel.SMS),
    text: "SMS",
    color: BADGE_DOT_COLORS[CHANNEL_BADGE_MAP.SMS.variant],
  },
  {
    value: String(NotificationChannel.Push),
    text: "Push",
    color: BADGE_DOT_COLORS[CHANNEL_BADGE_MAP.Push.variant],
  },
];

export const CUSTOM_FILTER_OPTIONS: DropdownOption[] = [
  { value: "false", text: "Automatska" },
  { value: "true", text: "Prilagođena" },
];

/** Status u zapisniku slanja — iste boje kao badge u koloni. */
export const LOG_STATUS_FILTER_OPTIONS: DropdownOption[] = [
  { value: "true", text: "Poslano", color: BADGE_DOT_COLORS.success },
  { value: "false", text: "Neuspjelo", color: BADGE_DOT_COLORS.danger },
];

export const initialNotificationLogSearchForm: NotificationLogSearchFormValues = {
  vehicleId: null,
  channel: null,
  isSuccess: null,
};

/*---------------------------------------------------------------
   SCOPE OPTIONS
-----------------------------------------------------------------*/
export const getScopeOptions = (entityLabel: string) => [
  {
    value: NotificationScope.Group,
    label: "Grupno",
    description: `npr. ${entityLabel.toLowerCase()} - šaljemo notifikaciju za sve`,
    icon: "/media/icons/messages.svg",
  },
  {
    value: NotificationScope.Individual,
    label: "Individualno",
    description: `npr. ${entityLabel.toLowerCase()} - vi odaberite za koje želite dobivati notifikacije`,
    icon: "/media/icons/message.svg",
  },
];

/** Isti izbor kao `getScopeOptions`, u obliku koji traži `CardSelect` — id je ondje string. */
export const getScopeCardOptions = (entityLabel: string): CardSelectOption[] =>
  getScopeOptions(entityLabel).map((option) => ({
    id: String(option.value),
    title: option.label,
    description: option.description,
    icon: option.icon,
  }));

/*---------------------------------------------------------------
   DEFAULT TEMPLATES PER ENTITY TYPE (used for new notifications)
-----------------------------------------------------------------*/
export const ENTITY_TYPE_DEFAULT_TEMPLATES: Record<number, { title: string; messageTemplate: string }> = {
  [NotificationEntityType.FireExtinguisher]: {
    title: "Istek vatrogasnog aparata",
    messageTemplate: "Vatrogasni aparat <strong>{{EntityName}}</strong> ističe <strong>{{ExpiryDate}}</strong>. Preostalo dana: <strong>{{DaysUntilExpiry}}</strong>.",
  },
  [NotificationEntityType.VehicleRegistration]: {
    title: "Istek registracije vozila",
    messageTemplate: "Registracija vozila <strong>{{EntityName}}</strong> ističe <strong>{{ExpiryDate}}</strong>. Preostalo dana: <strong>{{DaysUntilExpiry}}</strong>.",
  },
  [NotificationEntityType.VehicleInsurance]: {
    title: "Istek osiguranja vozila",
    messageTemplate: "Osiguranje vozila <strong>{{EntityName}}</strong> ističe <strong>{{ExpiryDate}}</strong>. Preostalo dana: <strong>{{DaysUntilExpiry}}</strong>.",
  },
  [NotificationEntityType.Offer]: {
    title: "Istek ponude",
    messageTemplate: "Ponuda <strong>{{EntityName}}</strong> ističe <strong>{{ExpiryDate}}</strong>. Preostalo dana: <strong>{{DaysUntilExpiry}}</strong>.",
  },
  [NotificationEntityType.Contract]: {
    title: "Istek ugovora",
    messageTemplate: "Ugovor <strong>{{EntityName}}</strong> ističe <strong>{{ExpiryDate}}</strong>. Preostalo dana: <strong>{{DaysUntilExpiry}}</strong>.",
  },
  [NotificationEntityType.Invoice]: {
    title: "Dospijeće računa",
    messageTemplate: "Račun <strong>{{EntityName}}</strong> dospijeva <strong>{{ExpiryDate}}</strong>. Preostalo dana: <strong>{{DaysUntilExpiry}}</strong>.",
  },
  [NotificationEntityType.Custom]: {
    title: "Prilagođena obavijest",
    messageTemplate: "",
  },
};

/*---------------------------------------------------------------
   MESSAGE TEMPLATE TAGS
-----------------------------------------------------------------*/
export const MESSAGE_TEMPLATE_TAGS: TemplateTag[] = [
  { key: "EntityName", label: "Naziv entiteta", description: "Naziv entiteta na koji se notifikacija odnosi" },
  { key: "ExpiryDate", label: "Datum isteka", description: "Datum isteka entiteta" },
  { key: "DaysUntilExpiry", label: "Dani do isteka", description: "Broj dana do isteka entiteta" },
];
